import React, { useMemo, useState } from 'react';
import { useLanguage } from '@/hooks/use-language';
import { useEvents } from '@/hooks/use-events';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Calendar, MapPin, Users, Pencil, Repeat } from 'lucide-react';
import { format } from 'date-fns';
import { EditEventDialog } from './EditEventDialog';
import type { EventData } from '@/contexts/event/EventContext';

export function EventListView() {
  const { t } = useLanguage();
  const { events } = useEvents();
  const [searchQuery, setSearchQuery] = useState('');
  const [timeFilter, setTimeFilter] = useState<'all' | 'upcoming' | 'past'>('upcoming');
  const [selectedEvent, setSelectedEvent] = useState<EventData | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);

  const filteredEvents = useMemo(() => {
    const now = new Date();
    
    return events
      .filter(event => {
        // Time filter
        if (timeFilter === 'upcoming' && new Date(event.date) < now) return false;
        if (timeFilter === 'past' && new Date(event.date) >= now) return false;

        // Search filter
        if (searchQuery) {
          const query = searchQuery.toLowerCase();
          const title = (event.titleKey ? t(event.titleKey) : event.title).toLowerCase();
          const location = event.location.toLowerCase();
          return title.includes(query) || location.includes(query);
        }

        return true;
      })
      .sort((a, b) => {
        // Sort by date (most recent first for past, soonest first for upcoming)
        const dateA = new Date(a.date).getTime();
        const dateB = new Date(b.date).getTime();
        return timeFilter === 'past' ? dateB - dateA : dateA - dateB;
      });
  }, [events, searchQuery, timeFilter, t]);

  const handleEdit = (event: EventData) => {
    setSelectedEvent(event);
    setEditDialogOpen(true);
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            {t('admin.event_list')}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Filters */}
          <div className="flex gap-4">
            <Input
              placeholder="Search events..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="max-w-sm"
            />
            <Select value={timeFilter} onValueChange={(value: any) => setTimeFilter(value)}>
              <SelectTrigger className="w-[200px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Events</SelectItem>
                <SelectItem value="upcoming">{t('admin.upcoming_events')}</SelectItem>
                <SelectItem value="past">{t('admin.past_events')}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Event List */}
          <div className="space-y-3">
            {filteredEvents.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                No events found
              </div>
            ) : (
              filteredEvents.map((event) => (
                <div
                  key={event.id}
                  className="flex items-center justify-between p-4 border rounded-lg hover:bg-muted/50 transition-colors"
                >
                  <div className="flex-1 space-y-2">
                    <div className="flex items-center gap-2">
                      <h4 className="font-semibold">
                        {event.titleKey ? t(event.titleKey) : event.title}
                      </h4>
                      {event.parentEventId && (
                        <Badge variant="outline" className="text-xs">
                          <Repeat className="h-3 w-3 mr-1" />
                          {t('event.recurring_badge')}
                        </Badge>
                      )}
                      <Badge variant={event.type === 'tournament' ? 'default' : 'secondary'}>
                        {event.type}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-4 text-sm text-muted-foreground">
                      <div className="flex items-center gap-1">
                        <Calendar className="h-4 w-4" />
                        {format(new Date(event.date), 'PPP')} at {event.time}
                      </div>
                      <div className="flex items-center gap-1">
                        <MapPin className="h-4 w-4" />
                        {event.location}
                      </div>
                      <div className="flex items-center gap-1">
                        <Users className="h-4 w-4" />
                        {event.participants.length}/{event.maxParticipants}
                      </div>
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleEdit(event)}
                  >
                    <Pencil className="h-4 w-4 mr-2" />
                    Edit
                  </Button>
                </div>
              ))
            )}
          </div>
        </CardContent>
      </Card>

      <EditEventDialog
        open={editDialogOpen}
        onOpenChange={setEditDialogOpen}
        event={selectedEvent}
        onEventUpdated={() => window.location.reload()}
      />
    </div>
  );
}
